import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowUpRight } from 'lucide-react';
import { getProxiedImageUrl } from '../utils/imageProxy';

const SWIPE_THRESHOLD = 80;

export default function SwipeCard({ business, onLike, onSkip, isTop }) {
  const cardRef     = useRef(null);
  const startRef    = useRef(null);
  const draggingRef = useRef(false);
  const [offset, setOffset]         = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [decision, setDecision]     = useState(null);

  if (!business) return null;

  const img  = business.immagini?.[0] ? getProxiedImageUrl(business.immagini[0]) : null;
  const logo = business.logo ? getProxiedImageUrl(business.logo) : null;

  const getPoint = (e) => {
    if (e.touches) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  };

  const onDragStart = (e) => {
    if (!isTop) return;
    startRef.current = getPoint(e);
    draggingRef.current = true;
    setIsDragging(true);
  };

  const onDragMove = (e) => {
    if (!draggingRef.current || !startRef.current) return;
    const pt = getPoint(e);
    const dx = pt.x - startRef.current.x;
    const dy = pt.y - startRef.current.y;
    setOffset({ x: dx, y: dy });
    if (dx > 20) setDecision('like');
    else if (dx < -20) setDecision('skip');
    else setDecision(null);
  };

  const flyOut = (dir) => {
    const targetX = dir === 'right' ? window.innerWidth : -window.innerWidth;
    setOffset({ x: targetX, y: offset.y });
    setTimeout(() => {
      if (dir === 'right') onLike(business);
      else onSkip(business);
    }, 300);
  };

  const onDragEnd = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setIsDragging(false);
    
    const { x } = offset;
    if (x > SWIPE_THRESHOLD) {
      flyOut('right');
    } else if (x < -SWIPE_THRESHOLD) {
      flyOut('left');
    } else {
      setOffset({ x: 0, y: 0 });
      setDecision(null);
    }
  };

  const rotate      = offset.x / 16;
  const likeOpacity = Math.min(1, offset.x > 0 ?  offset.x / SWIPE_THRESHOLD : 0);
  const nopeOpacity = Math.min(1, offset.x < 0 ? -offset.x / SWIPE_THRESHOLD : 0);

  const location = (() => {
    const { citta, provincia, provincia_codice } = business;
    if (citta && provincia && citta !== provincia) return `${citta} (${provincia_codice || provincia})`;
    if (citta) return citta;
    if (provincia) return provincia;
    return '';
  })();

  return (
    <div className="animate-card-entrance absolute inset-0" style={{ pointerEvents: 'none' }}>
      <div
        ref={cardRef}
        className="absolute inset-0"
        style={{
          borderRadius: '1.5rem',
          overflow: 'hidden',
          userSelect: 'none',
          pointerEvents: 'auto',
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          willChange: 'transform',
          transform: `translate(${offset.x}px, ${offset.y}px) rotate(${rotate}deg)`,
          transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.18), 0 8px 20px rgba(0,0,0,0.08), inset 0 1px 1px rgba(255,255,255,0.2)',
        }}
        onMouseDown={onDragStart}
        onMouseMove={onDragMove}
        onMouseUp={onDragEnd}
        onMouseLeave={onDragEnd}
        onTouchStart={onDragStart}
        onTouchMove={onDragMove}
        onTouchEnd={onDragEnd}
      >
        {/* Full-bleed photo */}
      {img ? (
        <img
          src={img}
          alt={business.nome}
          draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <div style={{
          width: '100%', height: '100%',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: '5rem', opacity: 0.2 }}>✦</span>
        </div>
      )}

      {/* Top gradient (for badges) */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, transparent 35%)',
        pointerEvents: 'none',
      }} />

      {/* Bottom gradient */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 40%, transparent 65%)',
        pointerEvents: 'none',
      }} />

      {/* SALVO badge */}
      <div style={{
        position: 'absolute', top: 24, left: 20,
        opacity: likeOpacity,
        transform: 'rotate(-10deg)',
        transition: 'opacity 0.08s',
        pointerEvents: 'none',
      }}>
        <span style={{
          display: 'inline-block',
          padding: '8px 18px',
          borderRadius: 8,
          border: '3px solid #22c55e',
          color: '#22c55e',
          fontSize: 22,
          fontWeight: 900,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          textShadow: '0 1px 4px rgba(0,0,0,0.3)',
        }}>
          SALVO ❤
        </span>
      </div>

      {/* SALTA badge */}
      <div style={{
        position: 'absolute', top: 24, right: 20,
        opacity: nopeOpacity,
        transform: 'rotate(10deg)',
        transition: 'opacity 0.08s',
        pointerEvents: 'none',
      }}>
        <span style={{
          display: 'inline-block',
          padding: '8px 18px',
          borderRadius: 8,
          border: '3px solid #ef4444',
          color: '#ef4444',
          fontSize: 22,
          fontWeight: 900,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          textShadow: '0 1px 4px rgba(0,0,0,0.3)',
        }}>
          SALTA ✕
        </span>
      </div>

      {/* Category + logo top row */}
      <div style={{
        position: 'absolute', top: 16, left: 0, right: 0,
        display: 'flex', justifyContent: 'flex-end',
        padding: '0 16px',
      }}>
        {business.categoria && (
          <span style={{
            padding: '5px 12px',
            borderRadius: 999,
            backgroundColor: 'rgba(255,255,255,0.18)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.25)',
            color: '#fff',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}>
            {business.categoria}
          </span>
        )}
      </div>

      {/* Bottom info overlay */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        padding: '20px 20px 24px',
      }}>
        {/* Logo + name row */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {logo && (
              <div style={{
                width: 44, height: 44,
                borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.8)',
                backgroundColor: '#fff',
                overflow: 'hidden',
                marginBottom: 8,
                boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
              }}>
                <img
                  src={logo}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }}
                  crossOrigin="anonymous"
                  onError={(e) => (e.target.style.display = 'none')}
                />
              </div>
            )}

            <h2 style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontSize: '1.6rem',
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              textShadow: '0 2px 8px rgba(0,0,0,0.4)',
            }}>
              {business.nome}
            </h2>

            {location && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 5 }}>
                <MapPin size={12} color="rgba(255,255,255,0.75)" />
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                  {location}
                </span>
              </div>
            )}

            {business.payoff && (
              <p style={{
                margin: '8px 0 0',
                fontSize: 13,
                color: 'rgba(255,255,255,0.7)',
                lineHeight: 1.45,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
                {business.payoff}
              </p>
            )}
          </div>

          {/* Dettagli button */}
          <Link
            to={`/attivita/${business.slug}`}
            onClick={(e) => e.stopPropagation()}
            style={{
              flexShrink: 0,
              width: 46, height: 46,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              textDecoration: 'none',
              transition: 'all 0.3s cubic-bezier(0.32,0.72,0,1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <ArrowUpRight size={18} color="#fff" />
          </Link>
        </div>
      </div>
    </div>
    </div>
  );
}
